import React, { useState, useEffect } from "react";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import LineGraph from "./LineGraph";
import ProgressBar from "components/ProgressBar";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Checkbox from "@material-ui/core/Checkbox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import { baseUrl } from "helpers/Constants";
import { useAuth } from "helpers/use-auth";
import moment from "moment";
import {
  extractChartData,
  dataWithToolTip,
  getMaxValues,
  createDailyChartData,
  mapByDayNumber,
  createLast7DaysData,
} from "helpers/utils";
import { dailyFeedsChart } from "variables/charts.js";
function maxValuesInit() {
  return {
    Nitrogen: 100,
    Phosphorus: 100,
    Potassium: 100,
    Ph: 100,
    Temp: 100,
    Moisture: 100,
    Conductivity: 100,
    SignalLevel: 100,
  };
}
const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginBottom: "10px",
    "& > * + *": {
      marginTop: theme.spacing(3),
    },
  },
}));
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
export default function DailyAnalytics() {
  const classes = useStyles();
  const auth = useAuth();
  const [dailyN, setDailyN] = useState({});
  const [dailyP, setDailyP] = useState({});
  const [dailyK, setDailyK] = useState({});
  const [dailyPh, setDailyPh] = useState({});
  const [dailyTemp, setDailyTemp] = useState({});
  const [dailyMoisture, setDailyMoisture] = useState({});
  const [dailyEc, setDailyEc] = useState({});
  const [maxDaily, setMaxDaily] = useState(maxValuesInit());
  const [inProgress, setInProgress] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const handleDeviceSelection = (e, values) => {
    e.preventDefault();
    setSelectedDevices(values.map((d) => d.id));
  };
  useEffect(() => {
    setInProgress(true);

    if (devices.length === 0) {
      axios.get(`${baseUrl}user-devices/${auth.user.id}`).then((response) => {
        setDevices(response.data.map((e) => e.device));
      });
    }
    let devicesToUse =
      selectedDevices.length === 0 ? devices.map((d) => d.id) : selectedDevices;

    axios
      .get(`${baseUrl}analytics/daily?devices=${devicesToUse.join()}`)
      .then((response) => {
        let dataArr = response.data.map((value) => [
          moment(value.date).format("YYYY-MM-DD"),
          value,
        ]);

        let dataObj = Object.fromEntries(dataArr);
        let dailyByDayNum = mapByDayNumber(dataObj);
        let dailyByDay = createLast7DaysData(dailyByDayNum);
        let dailyChartData = createDailyChartData(dailyByDay);

        setMaxDaily(getMaxValues(dailyChartData));
        setDailyN(
          dataWithToolTip(
            "N(mg/Kg)",
            extractChartData("Nitrogen", dailyChartData)
          )
        );
        setDailyP(
          dataWithToolTip(
            "P(mg/Kg)",
            extractChartData("Phosphorus", dailyChartData)
          )
        );
        setDailyK(
          dataWithToolTip(
            "K(mg/Kg)",
            extractChartData("Potassium", dailyChartData)
          )
        );
        setDailyPh(
          dataWithToolTip("Ph", extractChartData("Ph", dailyChartData))
        );
        setDailyTemp(
          dataWithToolTip("Temp(°C)", extractChartData("Temp", dailyChartData))
        );
        setDailyMoisture(
          dataWithToolTip(
            "Relative Humidity(%RH)",
            extractChartData("Moisture", dailyChartData)
          )
        );
        setDailyEc(
          dataWithToolTip(
            "EC(us/cm)",
            extractChartData("Conductivity", dailyChartData)
          )
        );
        setInProgress(false);
      })
      .catch((error) => {
        setInProgress(false);
        console.log("xxxx error", error);
      });
  }, [selectedDevices]);
  return (
    <div>
      <div className={classes.root}>
        <Autocomplete
          multiple
          // id="checkboxes-tags-demo"
          id="tags-standard"
          options={devices}
          disableCloseOnSelect
          getOptionLabel={(device) => device.number}
          onChange={handleDeviceSelection}
          renderOption={(device, { selected }) => (
            <React.Fragment>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {`${device.farm.name}-${device.farm.crop.name}(${device.number})`}
            </React.Fragment>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Select Devices"
              placeholder="Leave empty to use all"
            />
          )}
        />
      </div>
      {inProgress && <ProgressBar></ProgressBar>}
      <GridContainer>
        <GridItem xs={12} sm={12} md={4}>
          <LineGraph
            title="Nitrogen"
            periodSlug="Last 7 Days"
            options={dailyFeedsChart.options}
            max={maxDaily.Nitrogen}
            data={dailyN}
            color="success"
            listener={dailyFeedsChart.animation}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={4}>
          <LineGraph
            title="Phosphorus"
            periodSlug="Last 7 Days"
            options={dailyFeedsChart.options}
            max={maxDaily.Phosphorus}
            data={dailyP}
            color="success"
            listener={dailyFeedsChart.animation}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={4}>
          <LineGraph
            title="Potassium"
            periodSlug="Last 7 Days"
            options={dailyFeedsChart.options}
            max={maxDaily.Potassium}
            data={dailyK}
            color="success"
            listener={dailyFeedsChart.animation}
          />
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={12} md={4}>
          <LineGraph
            title="Ph"
            periodSlug="Last 7 Days"
            options={dailyFeedsChart.options}
            max={maxDaily.Ph}
            data={dailyPh}
            color="success"
            listener={dailyFeedsChart.animation}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={4}>
          <LineGraph
            title="Temperature"
            periodSlug="Last 7 Days"
            options={dailyFeedsChart.options}
            max={maxDaily.Temp}
            data={dailyTemp}
            color="success"
            listener={dailyFeedsChart.animation}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={4}>
          <LineGraph
            title="Relative Humidity"
            periodSlug="Last 7 Days"
            options={dailyFeedsChart.options}
            max={maxDaily.Moisture}
            data={dailyMoisture}
            color="success"
            listener={dailyFeedsChart.animation}
          />
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={12} md={4}>
          <LineGraph
            title="Conductivity"
            periodSlug="Last 7 Days"
            options={dailyFeedsChart.options}
            max={maxDaily.Conductivity}
            data={dailyEc}
            color="success"
            listener={dailyFeedsChart.animation}
          />
        </GridItem>
      </GridContainer>
    </div>
  );
}
